import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useSafetyPlan } from "@/hooks/useSafetyPlan";
import { useState, useEffect } from "react";
import { SafetyPlanData } from "@/hooks/useSafetyPlan";
import { Skeleton } from "@/components/ui/skeleton";
import { Share2, Copy, Download, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import html2pdf from "html2pdf.js";
import DailyMoodTracker from "@/components/DailyMoodTracker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";

const SafetyPlan = () => {
  const { plan, allPlans, isLoading, saveSafetyPlan, toggleSharing, shareUrl, loadPlanById, handleNewPlan } = useSafetyPlan();
  const [localPlan, setLocalPlan] = useState<SafetyPlanData>(plan);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    setLocalPlan(plan);
  }, [plan]);

  const handleChange = (field: keyof SafetyPlanData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setLocalPlan(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveSafetyPlan(localPlan);
      toast({
        title: "Safety plan saved",
        description: "Your safety plan has been saved successfully",
      });
    } catch (error) {
      console.error('Error saving safety plan:', error);
      toast({
        title: "Error saving plan",
        description: "Failed to save your safety plan. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const copyShareLink = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: "Link copied",
        description: "Share link has been copied to clipboard",
      });
    }
  };

  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      const content = document.getElementById('safety-plan-content');
      if (!content) {
        throw new Error('Content not found');
      }

      const opt = {
        margin: 1,
        filename: 'safety-plan.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      await html2pdf().set(opt).from(content).save();
      
      toast({
        title: "PDF exported",
        description: "Your safety plan has been exported as a PDF",
      });
    } catch (error) {
      console.error('PDF export error:', error);
      toast({
        title: "Export failed",
        description: "Failed to export PDF. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 pt-24">
        <div className="max-w-3xl mx-auto space-y-6">
          <Skeleton className="h-8 w-48" />
          <Card className="p-4">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-32 w-full" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 pt-24">
      <div className="max-w-3xl mx-auto">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold">Safety Plan Builder</h1>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={handleNewPlan}
              className="flex items-center gap-2 flex-1 md:flex-none justify-center"
            >
              <Plus className="h-4 w-4" />
              New Plan
            </Button>
            <Button
              variant="outline"
              onClick={toggleSharing}
              className="flex items-center gap-2 flex-1 md:flex-none justify-center"
            >
              <Share2 className="h-4 w-4" />
              {plan.is_public ? "Make Private" : "Share Plan"}
            </Button>
            {shareUrl && (
              <Button
                variant="outline"
                onClick={copyShareLink}
                className="flex items-center gap-2 flex-1 md:flex-none justify-center"
              >
                <Copy className="h-4 w-4" />
                Copy Link
              </Button>
            )}
            <Button
              variant="outline"
              onClick={exportToPDF}
              disabled={isExporting}
              className="flex items-center gap-2 flex-1 md:flex-none justify-center"
            >
              <Download className="h-4 w-4" />
              {isExporting ? "Exporting..." : "Export PDF"}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={isSaving}
              className="bg-primary hover:bg-primary-hover text-primary-foreground flex-1 md:flex-none"
            >
              {isSaving ? "Saving..." : "Save Plan"}
            </Button>
          </div>
        </div>

        {allPlans.length > 0 && (
          <div className="mb-6">
            <Label htmlFor="planSelect">Select Safety Plan</Label>
            <Select
              value={plan.id}
              onValueChange={(value) => {
                console.log("Selected plan ID:", value);
                loadPlanById(value);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a safety plan" />
              </SelectTrigger>
              <SelectContent>
                {allPlans.map((p) => (
                  <SelectItem key={p.id} value={p.id || ""}>
                    {p.title || format(new Date(p.createdAt || ''), 'PPP')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-6">
          <DailyMoodTracker />
          
          <Card className="p-4 md:p-6" id="safety-plan-content">
            <div className="space-y-4 md:space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Plan Title</Label>
                <Input
                  id="title"
                  placeholder="Enter a name for your safety plan"
                  value={localPlan.title}
                  onChange={handleChange("title")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="warningSigns">Warning Signs</Label>
                <Textarea
                  id="warningSigns"
                  placeholder="What signs indicate you might be heading towards a crisis?"
                  value={localPlan.warningSigns}
                  onChange={handleChange("warningSigns")}
                  className="min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="copingStrategies">Coping Strategies</Label>
                <Textarea
                  id="copingStrategies"
                  placeholder="What helps you feel better when you're struggling?"
                  value={localPlan.copingStrategies}
                  onChange={handleChange("copingStrategies")}
                  className="min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contacts">Support Contacts</Label>
                <Textarea
                  id="contacts"
                  placeholder="List people you can reach out to (include phone numbers)"
                  value={localPlan.contacts}
                  onChange={handleChange("contacts")}
                  className="min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="professionals">Professional Resources</Label>
                <Textarea
                  id="professionals"
                  placeholder="List professional contacts and emergency numbers"
                  value={localPlan.professionals}
                  onChange={handleChange("professionals")}
                  className="min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="safeEnvironment">Making Environment Safe</Label>
                <Textarea
                  id="safeEnvironment"
                  placeholder="Steps to make your environment safer when in crisis"
                  value={localPlan.safeEnvironment}
                  onChange={handleChange("safeEnvironment")}
                  className="min-h-[100px]"
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SafetyPlan;
